import { ALL } from "@/constants";
import { isUnsetOrAllSelection, withAllSelectionFallback } from "@/helpers/all";
import { ProjectOutputFilterParams, ScenarioNode } from "@/settings/projectOutputFilters";

const isScenarioNode = (node: unknown): node is ScenarioNode => typeof node === "object" && node !== null;
const toId = (value: unknown): string => {
  if (value && typeof value === "object") {
    const node = value as { id?: unknown; value?: unknown };
    return String(node.id ?? node.value ?? "");
  }

  return String(value ?? "");
};

const flattenScenarioNodes = (nodes: Array<string | ScenarioNode>): Array<string | ScenarioNode> =>
  nodes.flatMap((node) =>
    isScenarioNode(node) && Array.isArray(node.children) && node.children.length
      ? flattenScenarioNodes(node.children)
      : [node]
  );

export const getDefaultScenarioSelection = (scenarioTree: ProjectOutputFilterParams["scenario"]): string[] => {
  if (!Array.isArray(scenarioTree)) return [];
  const result: string[] = [];

  for (const node of scenarioTree as Array<string | ScenarioNode>) {
    if (!isScenarioNode(node)) continue;
    const id = node.children?.[0]?.id;
    if (typeof id === "string" && id.length > 0) {
      result.push(id);
    }
  }

  return result;
};

export const normalizeScenarioSelection = (scenario: ProjectOutputFilterParams["scenario"]): string[] | null => {
  if (!Array.isArray(scenario)) return scenario;
  if (!scenario.length) return [];
  if (!isScenarioNode(scenario[0])) return scenario as string[];

  const result: string[] = [];

  for (const node of scenario as ScenarioNode[]) {
    const id = node?.id;
    if (typeof id === "string" && id.length > 0) {
      result.push(id);
    }
  }

  return result;
};

export const normalizeScenarioSet = (scenario: ProjectOutputFilterParams["scenario"]): Set<string> | null => {
  if (!Array.isArray(scenario) || scenario.length === 0) return null;
  if (scenario.some((item) => typeof item === "string" && item === ALL)) return null;

  const ids = flattenScenarioNodes(scenario).map(toId).filter(Boolean);
  return ids.length ? new Set(ids) : null;
};

export const getScenarioSelectionWithFallback = (scenarioTree: ProjectOutputFilterParams["scenario"]): string[] =>
  withAllSelectionFallback(getDefaultScenarioSelection(scenarioTree));

export const normalizeScenarioSelectionWithFallback = (
  scenario: ProjectOutputFilterParams["scenario"],
  scenarioTree: ProjectOutputFilterParams["scenario"]
): string[] => {
  const normalized = normalizeScenarioSelection(scenario);
  return isUnsetOrAllSelection(normalized) ? getScenarioSelectionWithFallback(scenarioTree) : normalized || [];
};
